import type { SocketMessage } from './types';

type Listener = (message: SocketMessage) => void;

/**
 * Cliente WebSocket para /api/socket do Traccar.
 *
 * Autenticação: usa o cookie JSESSIONID já estabelecido via POST /api/session.
 * Reconexão: tentativas com backoff exponencial limitado a 30s.
 *
 * Uso:
 *   const socket = createTraccarSocket();
 *   socket.connect();
 *   const off = socket.subscribe((msg) => console.log(msg));
 *   // ...
 *   off();
 *   socket.disconnect();
 */
export interface TraccarSocket {
  connect(): void;
  disconnect(): void;
  subscribe(listener: Listener): () => void;
  isConnected(): boolean;
}

function buildSocketUrl(): string {
  const override = import.meta.env.VITE_WS_URL;
  if (override) return override;
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${protocol}//${window.location.host}/api/socket`;
}

export function createTraccarSocket(): TraccarSocket {
  let ws: WebSocket | null = null;
  let listeners = new Set<Listener>();
  let shouldReconnect = false;
  let retryCount = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  const computeBackoff = () => Math.min(1000 * 2 ** retryCount, 30_000);

  const cleanup = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const connect = () => {
    cleanup();
    shouldReconnect = true;

    try {
      ws = new WebSocket(buildSocketUrl());
    } catch (err) {
      console.error('[socket] falha ao criar WebSocket', err);
      scheduleReconnect();
      return;
    }

    ws.onopen = () => {
      retryCount = 0;
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data as string) as SocketMessage;
        for (const l of listeners) l(data);
      } catch (err) {
        console.warn('[socket] mensagem inválida', err);
      }
    };

    ws.onerror = () => {
      // erros de WS não trazem detalhe útil; reagimos no onclose
    };

    ws.onclose = () => {
      ws = null;
      if (shouldReconnect) scheduleReconnect();
    };
  };

  const scheduleReconnect = () => {
    if (!shouldReconnect) return;
    retryCount += 1;
    const delay = computeBackoff();
    reconnectTimer = setTimeout(connect, delay);
  };

  const disconnect = () => {
    shouldReconnect = false;
    cleanup();
    ws?.close();
    ws = null;
  };

  return {
    connect,
    disconnect,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    isConnected() {
      return ws?.readyState === WebSocket.OPEN;
    },
  };
}
